import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, FileText, User, Phone, Car, Truck, Wrench, Calendar, Search, Check, Save, Printer, ShoppingBag } from 'lucide-react';

interface CartItem {
  product: { id: number; name: string; price: number; emoji?: string; coreCharge?: number };
  qty: number;
}

interface Customer {
  id: number;
  name: string;
  phone: string;
  points?: number | string;
  totalSpent?: number | string;
}

interface SaveEstimateModalProps {
  isOpen: boolean;
  onClose: () => void;
  cart: CartItem[];
  subtotal: number;
  discountPct: number;
  grandTotal: number;
  storeApiKey: string;  formatRs: (amount: number) => string;
  onSaved: (estimate: any, andPrint?: boolean) => void;
  preselectedCustomer?: Customer | null;
  shopType?: string;
  isAutoParts?: boolean;
}

export const SaveEstimateModal: React.FC<SaveEstimateModalProps> = ({
  isOpen,
  onClose,
  cart,
  subtotal,
  discountPct,
  grandTotal,
  storeApiKey,
  formatRs,
  onSaved,
  preselectedCustomer,
  shopType = 'RETAIL',
  isAutoParts = false,
}) => {
  // ── Customer picker state ──
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(preselectedCustomer || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Estimate mode (Auto Parts vs Grocery) — persisted to localStorage ──
  const storageKey = `kangavena:estimateMode:${storeApiKey}`;
  const [estimateMode, setEstimateMode] = useState<'auto' | 'grocery'>(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved === 'auto' || saved === 'grocery') return saved;
    return isAutoParts ? 'auto' : 'grocery';
  });
  const isAuto = estimateMode === 'auto';

  // ── Common form fields ──
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [validDays, setValidDays] = useState(7);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── Auto Parts fields ──
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [serviceType, setServiceType] = useState('Repair');

  // ── Grocery / Retail fields ──
  const [deliveryDate, setDeliveryDate] = useState('');
  const [orderType, setOrderType] = useState<'pickup' | 'delivery'>('pickup');

  // Pre-fill from preselected customer
  useEffect(() => {
    if (preselectedCustomer) {
      setSelectedCustomer(preselectedCustomer);
      setCustomerName(preselectedCustomer.name || '');
      setCustomerPhone(preselectedCustomer.phone || '');
    }
  }, [preselectedCustomer]);

  const reset = () => {
    setSelectedCustomer(preselectedCustomer || null);
    setCustomerName(preselectedCustomer?.name || '');
    setCustomerPhone(preselectedCustomer?.phone || '');
    setSearchQuery('');
    setSearchResults([]);
    setShowSearch(false);
    setNotes('');
    setValidDays(7);
    setSaving(false);
    setError('');
    // Auto fields
    setVehicleMake('');
    setVehicleModel('');
    setVehicleYear('');
    setLicensePlate('');
    setServiceType('Repair');
    // Grocery fields
    setDeliveryDate('');
    setOrderType('pickup');
  };

  const handleClose = () => { reset(); onClose(); };

  // ── Customer search ──
  const searchCustomers = useCallback((query: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (query.trim().length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('kangavena:cashierToken') || '';
        const res = await fetch(
          `/api/customers?q=${encodeURIComponent(query)}&limit=5`,
          {
            headers: {
              'x-store-api-key': storeApiKey,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.customers || []);
        }
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [storeApiKey]);

  const selectCustomer = (c: Customer) => {
    setSelectedCustomer(c);
    setCustomerName(c.name || '');
    setCustomerPhone(c.phone || '');
    setSearchQuery('');
    setSearchResults([]);
    setShowSearch(false);
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerName('');
    setCustomerPhone('');
  };

  // ── Save ──
  const saveEstimate = async (andPrint: boolean) => {
    setSaving(true);
    setError('');
    try {
      // Build extra fields based on store type
      const extraFields: any = {};
      if (isAutoParts) {
        extraFields.vehicleMake = vehicleMake.trim() || undefined;
        extraFields.vehicleModel = vehicleModel.trim() || undefined;
        extraFields.vehicleYear = vehicleYear.trim() || undefined;
        extraFields.licensePlate = licensePlate.trim() || undefined;
        extraFields.serviceType = serviceType;
      } else {
        // Grocery/Retail
        extraFields.deliveryDate = deliveryDate || undefined;
        extraFields.orderType = orderType;
      }

      const res = await fetch('/api/estimates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-store-api-key': storeApiKey,
        },
        body: JSON.stringify({
          customerId: selectedCustomer?.id || undefined,
          customerName: customerName.trim() || selectedCustomer?.name || undefined,
          customerPhone: customerPhone.trim() || selectedCustomer?.phone || undefined,
          ...extraFields,
          cartSnapshot: cart.map(i => ({
            product: { id: i.product.id, name: i.product.name, price: i.product.price, emoji: i.product.emoji, coreCharge: i.product.coreCharge },
            qty: i.qty,
          })),
          subtotal,
          discountPct,
          grandTotal,
          notes: notes.trim() || undefined,
          validDays,
        }),
      });
      const data = await res.json();
      const fullEstimate = {
        id: data.estimate.id,
        customerName: customerName.trim() || selectedCustomer?.name || undefined,
        customerPhone: customerPhone.trim() || selectedCustomer?.phone || undefined,
        vehicleMake: isAuto ? (vehicleMake.trim() || undefined) : undefined,
        vehicleModel: isAuto ? (vehicleModel.trim() || undefined) : undefined,
        vehicleYear: isAuto ? (vehicleYear.trim() || undefined) : undefined,
        licensePlate: isAuto ? (licensePlate.trim() || undefined) : undefined,
        serviceType: isAuto ? serviceType : undefined,
        orderType: !isAuto ? orderType : undefined,
        deliveryDate: !isAuto ? deliveryDate : undefined,
        cart: cart as any,
        subtotal,
        discountPct,
        grandTotal,
        notes: notes.trim() || undefined,
        validUntil: data.estimate.validUntil || new Date(Date.now() + validDays * 86400000).toISOString(),
        validDays,
        createdAt: data.estimate.createdAt || new Date().toISOString(),
      };
      onSaved(fullEstimate, andPrint);
      reset();
      onClose();
    } catch {
      setError('Network error — check connection');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10000] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 print:hidden"
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 350 }}
            className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-sky-50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-sky-100 border border-sky-200 flex items-center justify-center">
                  <FileText size={14} className="text-sky-600" />
                </div>
                <div>
                  <p className="text-[13px] font-black text-slate-800">
                    {isAuto ? 'Auto Parts Estimate' : 'Save Estimate'}
                  </p>
                  <p className="text-[9px] text-slate-500 font-mono">
                    {isAuto ? '🚗 Auto Parts' : '🛍️ Grocery'} · {cart.length} item{cart.length !== 1 ? 's' : ''} · {formatRs(grandTotal)}
                  </p>
                </div>
              </div>
              <button onClick={handleClose} className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors">
                <X size={13} />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[70vh] p-5 space-y-4">
              {/* ══════ ESTIMATE MODE SWITCHER ══════ */}
              <div className="flex gap-1.5 p-1 bg-slate-100 rounded-xl">
                <button
                  onClick={() => { setEstimateMode('auto'); localStorage.setItem(storageKey, 'auto'); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${
                    estimateMode === 'auto'
                      ? 'bg-white text-sky-600 shadow-sm border border-slate-200'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}>
                  <Car size={12} /> Auto Parts
                </button>
                <button
                  onClick={() => { setEstimateMode('grocery'); localStorage.setItem(storageKey, 'grocery'); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${
                    estimateMode === 'grocery'
                      ? 'bg-white text-sky-600 shadow-sm border border-slate-200'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}>
                  <ShoppingBag size={12} /> Grocery
                </button>
              </div>

              {/* ══════ CUSTOMER PICKER (all store types) ══════ */}
              <div className="space-y-2.5">
                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5"><User size={9} /> Customer</p>

                {selectedCustomer && (
                  <div className="flex items-center gap-2 p-2.5 rounded-xl bg-sky-50 border border-sky-200">
                    <div className="w-8 h-8 rounded-full bg-sky-500 text-white flex items-center justify-center text-[11px] font-black shrink-0">
                      {(selectedCustomer.name || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold text-slate-800 truncate">{selectedCustomer.name}</p>
                      <p className="text-[9px] text-slate-500 font-mono">{selectedCustomer.phone || 'No phone'}</p>
                    </div>
                    <button onClick={clearCustomer} className="w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-red-500 hover:border-red-200 transition-all shrink-0">
                      <X size={10} />
                    </button>
                  </div>
                )}

                {!selectedCustomer && (
                  <div className="relative">
                    <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={searchQuery}
                      onChange={e => { setSearchQuery(e.target.value); setShowSearch(true); searchCustomers(e.target.value); }}
                      onFocus={() => setShowSearch(true)}
                      placeholder="Search existing customer..."
                      className="w-full pl-8 pr-3 py-2.5 rounded-xl border border-slate-200 text-[11px] text-slate-800 placeholder:text-slate-300 focus:border-sky-400 focus:ring-1 focus:ring-sky-400/30 outline-none transition-all bg-slate-50"
                    />
                    {searching && <div className="absolute right-3 top-1/2 -translate-y-1/2"><div className="w-3.5 h-3.5 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" /></div>}
                    {showSearch && searchResults.length > 0 && (
                      <div className="absolute z-50 top-full mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-40 overflow-y-auto">
                        {searchResults.map(c => (
                          <button key={c.id} onClick={() => selectCustomer(c)} className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-sky-50 transition-colors text-left">
                            <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[10px] font-black shrink-0">{(c.name || '?')[0].toUpperCase()}</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-bold text-slate-800 truncate">{c.name}</p>
                              <p className="text-[9px] text-slate-400 font-mono">{c.phone || 'No phone'}</p>
                            </div>
                            <Check size={12} className="text-sky-500 shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!selectedCustomer && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] font-bold text-slate-500 mb-1 block">Name</label>
                      <input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="e.g. Kamal Perera"
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[11px] text-slate-800 placeholder:text-slate-300 focus:border-sky-400 focus:ring-1 focus:ring-sky-400/30 outline-none transition-all bg-slate-50" />
                    </div>
                    <div>
                      <label className="text-[9px] font-bold text-slate-500 mb-1 block">Phone</label>
                      <input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="07X XXX XXXX" type="tel"
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[11px] text-slate-800 placeholder:text-slate-300 focus:border-sky-400 focus:ring-1 focus:ring-sky-400/30 outline-none transition-all bg-slate-50" />
                    </div>
                  </div>
                )}
              </div>

              {/* ══════ AUTO PARTS: Vehicle + Service ══════ */}
              {isAuto && (
                <div className="space-y-2.5">
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5"><Car size={9} /> Vehicle Information</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] font-bold text-slate-500 mb-1 block">Make</label>
                      <input value={vehicleMake} onChange={e => setVehicleMake(e.target.value)} placeholder="Toyota"
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[11px] text-slate-800 placeholder:text-slate-300 focus:border-sky-400 focus:ring-1 focus:ring-sky-400/30 outline-none transition-all bg-slate-50" />
                    </div>
                    <div>
                      <label className="text-[9px] font-bold text-slate-500 mb-1 block">Model</label>
                      <input value={vehicleModel} onChange={e => setVehicleModel(e.target.value)} placeholder="Prius"
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[11px] text-slate-800 placeholder:text-slate-300 focus:border-sky-400 focus:ring-1 focus:ring-sky-400/30 outline-none transition-all bg-slate-50" />
                    </div>
                    <div>
                      <label className="text-[9px] font-bold text-slate-500 mb-1 block">Year</label>
                      <input value={vehicleYear} onChange={e => setVehicleYear(e.target.value)} placeholder="2019"
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[11px] text-slate-800 placeholder:text-slate-300 focus:border-sky-400 focus:ring-1 focus:ring-sky-400/30 outline-none transition-all bg-slate-50" />
                    </div>
                    <div>
                      <label className="text-[9px] font-bold text-slate-500 mb-1 block">License Plate</label>
                      <input value={licensePlate} onChange={e => setLicensePlate(e.target.value)} placeholder="CAB-1234"
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[11px] text-slate-800 placeholder:text-slate-300 focus:border-sky-400 focus:ring-1 focus:ring-sky-400/30 outline-none transition-all bg-slate-50" />
                    </div>
                  </div>
                  {/* Service Type */}
                  <div>
                    <label className="text-[9px] font-bold text-slate-500 mb-1.5 flex items-center gap-1.5"><Wrench size={9} /> Service Type</label>
                    <div className="flex gap-1.5 flex-wrap">
                      {['Repair', 'Maintenance', 'Inspection', 'Parts Only', 'Other'].map(t => (
                        <button key={t} onClick={() => setServiceType(t)}
                          className={`px-3 py-1.5 rounded-xl text-[10px] font-bold border transition-all ${
                            serviceType === t
                              ? 'bg-sky-500 text-white border-sky-500 shadow-sm'
                              : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-sky-300'
                          }`}>{t}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ══════ GROCERY / RETAIL: Delivery + Order Type ══════ */}
              {!isAuto && (
                <div className="space-y-2.5">
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5"><ShoppingBag size={9} /> Order Details</p>
                  {/* Order Type */}
                  <div>
                    <label className="text-[9px] font-bold text-slate-500 mb-1.5 flex items-center gap-1.5">Type</label>
                    <div className="flex gap-2">
                      <button onClick={() => setOrderType('pickup')}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[10px] font-bold border transition-all ${
                          orderType === 'pickup'
                            ? 'bg-sky-500 text-white border-sky-500 shadow-sm'
                            : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-sky-300'
                        }`}>
                        <ShoppingBag size={11} /> Pickup
                      </button>
                      <button onClick={() => setOrderType('delivery')}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[10px] font-bold border transition-all ${
                          orderType === 'delivery'
                            ? 'bg-sky-500 text-white border-sky-500 shadow-sm'
                            : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-sky-300'
                        }`}>
                        <Truck size={11} /> Delivery
                      </button>
                    </div>
                  </div>
                  {/* Delivery Date */}
                  {orderType === 'delivery' && (
                    <div>
                      <label className="text-[9px] font-bold text-slate-500 mb-1 flex items-center gap-1.5"><Calendar size={9} /> Preferred Delivery Date</label>
                      <input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[11px] text-slate-800 focus:border-sky-400 focus:ring-1 focus:ring-sky-400/30 outline-none transition-all bg-slate-50" />
                    </div>
                  )}
                </div>
              )}

              {/* ══════ VALIDITY (all store types) ══════ */}
              <div className="space-y-2.5">
                <div>
                  <label className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 mb-1.5 flex items-center gap-1.5"><Calendar size={9} /> Valid For</label>
                  <div className="flex gap-2">
                    {[3, 7, 14, 30].map(d => (
                      <button key={d} onClick={() => setValidDays(d)}
                        className={`flex-1 py-2 rounded-xl text-[10px] font-black border transition-all ${
                          validDays === d
                            ? 'bg-sky-500 text-white border-sky-500 shadow-sm'
                            : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-sky-300'
                        }`}>{d}d</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[9px] font-bold text-slate-500 mb-1 block">
                    {isAuto ? 'Notes / Special Instructions' : 'Notes / Special Requests'}
                  </label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)}
                    placeholder={isAuto ? 'Parts availability, urgency, warranty notes...' : 'Special requests, dietary needs, gift wrapping...'}
                    rows={2}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[11px] text-slate-800 placeholder:text-slate-300 focus:border-sky-400 focus:ring-1 focus:ring-sky-400/30 outline-none transition-all bg-slate-50 resize-none" />
                </div>
              </div>

              {error && <p className="text-[10px] text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>}
            </div>

            {/* Footer Actions */}
            <div className="px-5 pb-5 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2.5">
              <motion.button whileTap={{ scale: 0.97 }} onClick={() => saveEstimate(false)} disabled={saving || cart.length === 0}
                className="flex items-center justify-center gap-2 py-3 rounded-2xl font-black text-[11px] uppercase tracking-wider bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 transition-all disabled:opacity-50">
                <Save size={13} /> {saving ? 'Saving…' : 'Save Only'}
              </motion.button>
              <motion.button whileTap={{ scale: 0.97 }} onClick={() => saveEstimate(true)} disabled={saving || cart.length === 0}
                className="flex items-center justify-center gap-2 py-3 rounded-2xl font-black text-[11px] uppercase tracking-wider bg-gradient-to-r from-sky-500 to-cyan-500 text-white shadow-lg shadow-sky-500/25 transition-all disabled:opacity-50">
                <Printer size={13} /> {saving ? 'Saving…' : 'Save & Print'}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
